import React, { useRef, useState } from 'react';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import Reveal from '../motion/Reveal';
import ScrollSequence from '../motion/ScrollSequence';
import './ProcessSection.css';

const FRAME_COUNT = 102;

const processSteps = [
  {
    step: '01',
    title: 'Discovery',
    description:
      'We read the corpus, talk to your operators, and write down the questions the system actually has to answer. The eval set we build here outlives the engagement.',
    details: [
      'Stakeholder interviews and ground-truth gathering',
      'Eval set definition (real prompts, golden answers)',
      'Scope cut to one end-to-end vertical',
      'Risk and integration assessment',
    ],
  },
  {
    step: '02',
    title: 'Architecture',
    description:
      'We pick FastAPI vs. Django, agentic vs. pipeline RAG, pgvector vs. Pinecone against your data and constraints. We write the decision down so the next engineer does not have to re-derive it.',
    details: [
      'Reference stack tuned to your data',
      'Back end choice (FastAPI / Django) by gravity, not preference',
      'Vector store and retrieval strategy chosen on real corpus',
      'Auth, deploy target, and observability decided up front',
    ],
  },
  {
    step: '03',
    title: 'Build',
    description:
      'Next.js front end, FastAPI or Django back end, Celery workers, Postgres + pgvector, all running behind a real preview URL by the end of week two. Every prompt and retrieval change runs through the eval harness before it ships.',
    details: [
      'Next.js 16 App Router, RSC where it pays',
      'Async FastAPI or Django, Celery + Redis for background work',
      'Eval-driven iteration; no merging on vibes',
      'Trace observability via LangSmith or Langfuse',
    ],
  },
  {
    step: '04',
    title: 'Ship',
    description:
      'Production hardening, prompt-injection defenses, cost ceilings, structured logs, and a runbook your team owns. We pair on the handoff so the on-call rotation can actually debug what we built.',
    details: [
      'Auth review, rate limits, prompt-injection defenses',
      'Deploy automation and rollback documented in the repo',
      'Runbook, load-test report, and 90-day backlog handed over',
      'Optional retainer for iteration after launch',
    ],
  },
];

const ProcessSection = () => {
  const scrollerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  // Progress through the two-column scroller drives both the pinned image
  // sequence and which step is highlighted.
  const { scrollYProgress } = useScroll({
    target: scrollerRef,
    offset: ['start 75%', 'end 35%'],
  });
  useMotionValueEvent(scrollYProgress, 'change', (value) => setProgress(value));

  const stepCount = processSteps.length;
  const activeIndex = Math.min(stepCount - 1, Math.floor(progress * stepCount));

  return (
    <section id="methodology" className="process-section section">
      <div className="container">
        <Reveal className="section-header">
          <div className="section-badge">
            <span className="badge-icon">▣</span>
            How we work
          </div>
          <h2 className="section-title">
            Discovery, architecture, build, <span className="logo-highlight">ship</span>
          </h2>
          <p className="section-subtitle">
            A short engagement shape that fits AI products. No twelve-week discovery, no slide decks. We measure first, build against an eval set, and hand over a runbook your team can actually own.
          </p>
        </Reveal>

        <div className="process-scroller" ref={scrollerRef}>
          <div className="process-visual">
            <div className="process-visual-sticky">
              <ScrollSequence
                progress={scrollYProgress}
                frameCount={FRAME_COUNT}
                basePath="/media/process"
                ext="webp"
                width={960}
                height={540}
                className="process-canvas"
              />
              <div className="process-progress" aria-hidden="true">
                {processSteps.map((step, index) => {
                  const fill = Math.min(1, Math.max(0, progress * stepCount - index));
                  return <span key={step.step} style={{ '--fill': fill }} />;
                })}
              </div>
            </div>
          </div>

          <ol className="process-steps">
            {processSteps.map((step, index) => (
              <li
                key={step.step}
                className={`process-step${index === activeIndex ? ' is-active' : ''}`}
              >
                <Reveal className="process-step-inner" y={20}>
                  <div className="process-step-number">Step {step.step}</div>
                  <h3 className="process-title">{step.title}</h3>
                  <p className="process-description">{step.description}</p>
                  <ul className="process-details">
                    {step.details.map((detail) => (
                      <li key={detail} className="process-detail">
                        {detail}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
